/**
 * Operation Manifest Service
 *
 * Service for fetching and parsing operation manifests.
 * Provides settings availability, parameter definitions, and connection requirements.
 */

import type {
  OperationManifest,
  OperationManifestSettings,
  OperationInfo,
  SettingScope,
} from '@/types/operationManifest';
import {
  isSettingSupported,
  getOperationScope,
} from '@/types/operationManifest';
import type { NodeSettings, SettingData } from '@/types/nodeSettings';
import {
  DEFAULT_RETRY_POLICY,
  DEFAULT_CONCURRENCY,
} from '@/types/nodeSettings';
import type { ParameterInfo, ParameterGroup, DynamicSchemaConfig } from '@/types/operationParameter';
import {
  ParameterType,
  ParameterVisibility,
  ParameterEditor,
  DynamicLoadStatus,
} from '@/types/operationParameter';
import type { SchemaObject } from '@/types/valueSegment';
import { isPropertyRequired } from '@/types/valueSegment';
import type { SwaggerSpec } from '@/types/swagger';
import { SwaggerParser } from '@/parsers/SwaggerParser';
import { getBuiltInManifest } from '@/data/manifests';
import { optionalBracketAccessor } from '@/utils/expressions';
import {
  schemaTypeToParameterType,
  mapXmsEditorToParameterEditor,
} from '@/utils/schema';

// =============================================================================
// INPUT DEPENDENCY PARSING
// =============================================================================

/**
 * Input dependency format from manifest (type + parameters array)
 */
interface ManifestInputDependency {
  type?: 'visibility' | 'value';
  parameters?: Array<{
    name: string;
    values?: unknown[];
    excludeValues?: unknown[];
  }>;
}

/**
 * Input dependency format from swagger (parameter name as key)
 */
interface SwaggerInputDependency {
  [parameterName: string]: {
    type?: string;
    values?: unknown[];
    excludeValues?: unknown[];
  };
}

/**
 * Parsed input dependency ready for use
 */
interface ParsedInputDependency {
  type: 'visibility' | 'value';
  parameterReference: string;
  values?: unknown[];
  excludeValues?: unknown[];
}

/**
 * Parse x-ms-input-dependencies from either manifest or swagger format.
 *
 * Supports two formats:
 * 1. Manifest format: { type: 'visibility', parameters: [{ name: 'param', values: [...] }] }
 * 2. Swagger format: { 'paramName': { type: 'visibility', values: [...] } }
 *
 * @param inputDeps - The x-ms-input-dependencies value from schema
 * @returns Array of parsed input dependencies
 */
export function parseInputDependencies(inputDeps: unknown): ParsedInputDependency[] {
  if (!inputDeps || typeof inputDeps !== 'object') {
    return [];
  }

  const dependencies: ParsedInputDependency[] = [];

  // Check for manifest format: { type: 'visibility', parameters: [...] }
  const manifestFormat = inputDeps as ManifestInputDependency;
  if (manifestFormat.parameters && Array.isArray(manifestFormat.parameters)) {
    const depType: 'visibility' | 'value' = manifestFormat.type === 'value' ? 'value' : 'visibility';

    for (const param of manifestFormat.parameters) {
      if (param.name) {
        dependencies.push({
          type: depType,
          parameterReference: param.name,
          ...(param.values && { values: param.values }),
          ...(param.excludeValues && { excludeValues: param.excludeValues }),
        });
      }
    }

    return dependencies;
  }

  // Check for swagger format: { 'paramName': { type: 'visibility', values: [...] } }
  const swaggerFormat = inputDeps as SwaggerInputDependency;
  for (const [paramRef, config] of Object.entries(swaggerFormat)) {
    // Skip 'type' and 'parameters' keys which might be from manifest format
    if (paramRef === 'type' || paramRef === 'parameters') {
      continue;
    }

    if (config && typeof config === 'object') {
      const configType: 'visibility' | 'value' = config.type === 'value' ? 'value' : 'visibility';
      dependencies.push({
        type: configType,
        parameterReference: paramRef,
        ...(config.values && { values: config.values }),
        ...(config.excludeValues && { excludeValues: config.excludeValues }),
      });
    }
  }

  return dependencies;
}

// =============================================================================
// CACHE
// =============================================================================

const manifestCache = new Map<string, OperationManifest>();
const discoveryHintsCache = new Map<string, DiscoveryHints>();

/**
 * Generate cache key for an operation
 */
function getCacheKey(operationInfo: OperationInfo): string {
  return `${operationInfo.connectorId}:${operationInfo.operationId}`;
}

/**
 * Populate discovery hints from the operations list (fetched once per app).
 * Called from the useRuntimeManifestSync hook after built-in operations are loaded.
 */
export function setDiscoveryHints(operations: Array<{
  id: string;
  name: string;
  properties: DiscoveryHints & { api: { id: string } };
}>): void {
  discoveryHintsCache.clear();
  for (const op of operations) {
    // Key by api.id + operation name (matches connectorId:operationId pattern)
    const key = `${op.properties.api.id}:${op.name}`;
    discoveryHintsCache.set(key, op.properties);
    // Also key by just operation name for simpler lookups
    discoveryHintsCache.set(op.name, op.properties);
    discoveryHintsCache.set(op.id, op.properties);
  }
}

// =============================================================================
// RUNTIME MANIFEST FETCHING
// =============================================================================

/**
 * Auth context for runtime manifest fetching.
 * Set once when the app initializes with a selected Logic App.
 */
let runtimeFetchContext: {
  projectName: string;
  appName: string;
  getAccessToken: () => Promise<string | null>;
} | null = null;

/**
 * Configure runtime manifest fetching with the current app's auth context.
 * Call this when a Logic App is selected (app store initialization).
 */
export function setRuntimeManifestContext(context: {
  projectName: string;
  appName: string;
  getAccessToken: () => Promise<string | null>;
} | null): void {
  runtimeFetchContext = context;
}

/**
 * Try to fetch a manifest from the Logic Apps runtime API.
 * Uses the same $expand=properties/manifest endpoint as LogicAppsUX.
 * Returns null if runtime is unavailable or operation not found.
 */
async function fetchRuntimeManifest(operationInfo: OperationInfo): Promise<OperationManifest | null> {
  if (!runtimeFetchContext) return null;

  // Feature flag: allow disabling runtime manifest fetching
  if (import.meta.env.VITE_USE_RUNTIME_MANIFESTS === 'false') return null;

  try {
    const { fetchServiceProviderOperationManifest } = await import('@/services/logicAppsApi');

    const response = await fetchServiceProviderOperationManifest({
      projectName: runtimeFetchContext.projectName,
      appName: runtimeFetchContext.appName,
      getToken: runtimeFetchContext.getAccessToken,
      operationGroup: operationInfo.connectorId,
      operationId: operationInfo.operationId,
    });

    if (!response?.properties) return null;

    // Determine if this is a trigger — check operationType field and visibility hint
    const props = response.properties as Record<string, unknown>;
    const isTrigger =
      (typeof props.operationType === 'string' && props.operationType.toLowerCase() === 'trigger') ||
      (typeof props.visibility === 'string' && props.visibility.toLowerCase().includes('trigger'));

    return serviceProviderOperationToManifest(response.properties, isTrigger);
  } catch (error) {
    // Runtime fetch is best-effort — silently fall back to default
    console.debug(`[getManifest] Runtime fetch failed for ${operationInfo.connectorId}/${operationInfo.operationId}:`, error);
    return null;
  }
}

// =============================================================================
// MANIFEST FETCHING
// =============================================================================

/**
 * Get manifest for an operation (cached)
 *
 * Resolution order:
 * 1. Cache (in-memory)
 * 2. Built-in static manifests (hand-tuned, polished UX)
 * 3. Runtime API fetch (functional, auto-discovers all operations)
 * 4. Generic default manifest (safety net)
 */
export async function getManifest(operationInfo: OperationInfo): Promise<OperationManifest> {
  const cacheKey = getCacheKey(operationInfo);

  if (manifestCache.has(cacheKey)) {
    return manifestCache.get(cacheKey)!;
  }

  // Check for built-in manifest first (highest quality — hand-tuned with proper editors)
  const operationType = extractOperationType(operationInfo);
  const operationKind = extractOperationKind(operationInfo);

  if (operationType) {
    const builtInManifest = getBuiltInManifest(operationType, operationKind);
    if (builtInManifest) {
      manifestCache.set(cacheKey, builtInManifest);
      return builtInManifest;
    }
  }

  // Try runtime API (auto-discovers operations we don't have static manifests for)
  const runtimeManifest = await fetchRuntimeManifest(operationInfo);
  if (runtimeManifest) {
    manifestCache.set(cacheKey, runtimeManifest);
    return runtimeManifest;
  }

  if (import.meta.env.DEV) {
    console.warn(
      `[OperationManifest] No runtime manifest for ${operationInfo.connectorId}/${operationInfo.operationId}, using default manifest`
    );
  }

  // Fall back to generic default manifest, enriched with discovery hints if available
  const hints = discoveryHintsCache.get(cacheKey) ??
    discoveryHintsCache.get(operationInfo.operationId) ??
    discoveryHintsCache.get(operationInfo.connectorId);
  const manifest = createDefaultManifest(operationInfo, hints);
  manifestCache.set(cacheKey, manifest);

  return manifest;
}

/**
 * Extract the operation type from operation info.
 * For built-in operations, this is usually the operationId (e.g., "If", "Foreach").
 * For managed connectors, this might be derived from the connectorId.
 */
function extractOperationType(operationInfo: OperationInfo): string | null {
  // For built-in operations, the operationId is typically the type
  if (operationInfo.operationId) {
    return operationInfo.operationId;
  }
  return null;
}

/**
 * Extract the operation kind from operation info.
 * Kind is used for variations like Http vs Http/trigger.
 */
function extractOperationKind(operationInfo: OperationInfo): string | undefined {
  // Check if the connector indicates a trigger
  if (operationInfo.connectorId?.includes('/trigger')) {
    return 'trigger';
  }
  return undefined;
}

// =============================================================================
// TYPE-BASED MANIFEST SETTINGS
// =============================================================================

/**
 * Settings configuration for different operation types
 * Each type specifies which settings are supported
 */
const SETTINGS_BY_OPERATION_TYPE: Record<string, OperationManifestSettings> = {
  // HTTP/API actions - full networking support
  Http: {
    timeout: { scopes: ['action'] },
    retryPolicy: { scopes: ['action'] },
    requestOptions: { scopes: ['action'] },
    paging: { scopes: ['action'] },
    chunking: { scopes: ['action'] },
    downloadChunking: { scopes: ['action'] },
    concurrency: { scopes: ['action'] },
    secureData: {},
    trackedProperties: {},
    operationOptions: {
      scopes: ['action'],
      options: ['Asynchronous', 'DisableAsyncPattern', 'SuppressWorkflowHeaders'],
    },
  },

  // HTTP Response action
  Response: {
    timeout: { scopes: ['action'] },
    secureData: {},
    trackedProperties: {},
  },

  // HTTP Request trigger
  Request: {
    concurrency: { scopes: ['trigger'] },
    correlation: { scopes: ['trigger'] },
    secureData: {},
    trackedProperties: {},
    operationOptions: {
      scopes: ['trigger'],
      options: ['EnableSchemaValidation'],
    },
  },

  // Agent actions - timeout and retry, but simpler networking
  Agent: {
    timeout: { scopes: ['action'] },
    retryPolicy: { scopes: ['action'] },
    secureData: {},
    trackedProperties: {},
  },

  // Recurrence trigger
  Recurrence: {
    concurrency: { scopes: ['trigger'] },
    secureData: {},
    trackedProperties: {},
  },

  // ForEach - concurrency control
  Foreach: {
    concurrency: { scopes: ['action'] },
    timeout: { scopes: ['action'] },
    trackedProperties: {},
  },

  // Until loop - count and timeout
  Until: {
    count: { scopes: ['action'] },
    timeout: { scopes: ['action'] },
    trackedProperties: {},
  },

  // Switch/Condition - minimal settings
  Switch: {
    timeout: { scopes: ['action'] },
    trackedProperties: {},
  },

  If: {
    timeout: { scopes: ['action'] },
    trackedProperties: {},
  },

  // Scope - timeout only
  Scope: {
    timeout: { scopes: ['action'] },
    trackedProperties: {},
  },

  // Compose - minimal settings
  Compose: {
    secureData: {},
    trackedProperties: {},
  },

  // Parse JSON - minimal settings
  ParseJson: {
    secureData: {},
    trackedProperties: {},
  },

  // Variable operations
  InitializeVariable: {
    trackedProperties: {},
  },

  SetVariable: {
    trackedProperties: {},
  },

  IncrementVariable: {
    trackedProperties: {},
  },

  AppendToArrayVariable: {
    trackedProperties: {},
  },

  AppendToStringVariable: {
    trackedProperties: {},
  },

  // Wait/Delay
  Wait: {
    timeout: { scopes: ['action'] },
    trackedProperties: {},
  },

  // Terminate - minimal settings
  Terminate: {
    trackedProperties: {},
  },
};

/**
 * Default settings for unknown operation types.
 * Note: retryPolicy is intentionally excluded here — matching LogicAppsUX behavior
 * where only apiconnection, apiconnectionwebhook, and http types get retry policy
 * when no manifest is available. Operations with explicit manifests declare their
 * own retryPolicy support in the manifest settings.
 */
const DEFAULT_ACTION_SETTINGS: OperationManifestSettings = {
  timeout: { scopes: ['action'] },
  concurrency: { scopes: ['action'] },
  secureData: {},
  trackedProperties: {},
};

const DEFAULT_TRIGGER_SETTINGS: OperationManifestSettings = {
  concurrency: { scopes: ['trigger'] },
  correlation: { scopes: ['trigger'] },
  secureData: {},
  trackedProperties: {},
};

/**
 * Get settings for a specific operation type
 */
function getSettingsForOperationType(operationType: string, isTrigger: boolean): OperationManifestSettings {
  // Normalize type name (handle variations like 'http' vs 'Http')
  const normalizedType = operationType.charAt(0).toUpperCase() + operationType.slice(1);

  // Check for exact match first
  const exactMatch = SETTINGS_BY_OPERATION_TYPE[normalizedType];
  if (exactMatch) {
    return exactMatch;
  }

  // Check for partial matches (e.g., 'ApiConnection' contains patterns)
  if (operationType.toLowerCase().includes('http') || operationType.toLowerCase().includes('apiconnection')) {
    return SETTINGS_BY_OPERATION_TYPE['Http']!;
  }

  if (operationType.toLowerCase().includes('agent')) {
    return SETTINGS_BY_OPERATION_TYPE['Agent']!;
  }

  // Return defaults based on scope
  return isTrigger ? DEFAULT_TRIGGER_SETTINGS : DEFAULT_ACTION_SETTINGS;
}

/**
 * Default icons by connector category, preventing blank icon squares
 * when no icon is available from discovery or static manifests.
 */
const CATEGORY_ICON_FALLBACKS: Record<string, string> = {
  http: 'https://logicappsv2resources.blob.core.windows.net/icons/http.svg',
  schedule: 'https://logicappsv2resources.blob.core.windows.net/icons/recurrence.svg',
  control: 'https://logicappsv2resources.blob.core.windows.net/icons/control.svg',
  variable: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
  dataoperation: 'https://logicappsv2resources.blob.core.windows.net/icons/compose.svg',
  request: 'https://logicappsv2resources.blob.core.windows.net/icons/request.svg',
  agent: 'https://logicappsv2resources.blob.core.windows.net/icons/agent.svg',
  inlinecode: 'https://logicappsv2resources.blob.core.windows.net/icons/JavaScriptCode.svg',
  workflow: 'https://logicappsv2resources.blob.core.windows.net/icons/workflow.svg',
  servicebus: 'https://logicappsv2resources.blob.core.windows.net/icons/servicebus.svg',
};

/**
 * Resolve the best icon for an operation, checking (in order):
 * 1. Discovery operation iconUri
 * 2. Discovery API (connector) iconUri
 * 3. Category-based fallback from connector ID
 * 4. Default Azure Logic Apps icon
 */
function resolveIcon(
  connectorId: string,
  discoveryIconUri?: string,
  discoveryApiIconUri?: string
): string {
  if (discoveryIconUri) return discoveryIconUri;
  if (discoveryApiIconUri) return discoveryApiIconUri;

  // Match connector ID to a category fallback
  const lower = connectorId.toLowerCase();
  for (const [category, icon] of Object.entries(CATEGORY_ICON_FALLBACKS)) {
    if (lower.includes(category)) return icon;
  }

  // Generic Logic Apps icon
  return 'https://logicappsv2resources.blob.core.windows.net/icons/logicapp.svg';
}

/**
 * Normalize a brand color from discovery (can be number or string).
 */
function normalizeBrandColor(color: unknown): string {
  if (typeof color === 'string' && color.startsWith('#')) return color;
  if (typeof color === 'number') return `#${color.toString(16).padStart(6, '0')}`;
  return '#0078D4';
}

/**
 * Optional discovery data that can enrich the default manifest.
 * Matches the shape of properties from fetchBuiltInOperations().
 */
export interface DiscoveryHints {
  iconUri?: string;
  brandColor?: string | number;
  description?: string;
  summary?: string;
  api?: {
    iconUri?: string;
    brandColor?: string;
    displayName?: string;
    description?: string;
  };
  operationType?: string;
  trigger?: string;
  capabilities?: string[];
}

/**
 * Create a default manifest for operations without explicit manifests.
 * When discoveryHints are provided (from the operations API), the manifest
 * gets icons, brand colors, and descriptions instead of blank defaults.
 */
function createDefaultManifest(
  operationInfo: OperationInfo,
  discoveryHints?: DiscoveryHints
): OperationManifest {
  const isTrigger = operationInfo.operationId.toLowerCase().includes('trigger') ||
    operationInfo.operationId.toLowerCase().includes('when') ||
    discoveryHints?.operationType?.toLowerCase() === 'trigger' ||
    !!discoveryHints?.trigger;

  const operationType = inferOperationType(operationInfo);

  const iconUri = resolveIcon(
    operationInfo.connectorId,
    discoveryHints?.iconUri as string | undefined,
    discoveryHints?.api?.iconUri
  );
  const brandColor = normalizeBrandColor(
    discoveryHints?.brandColor ?? discoveryHints?.api?.brandColor
  );
  const description = discoveryHints?.description ?? discoveryHints?.api?.description ?? '';
  const summary = discoveryHints?.summary ?? discoveryHints?.api?.displayName ?? operationInfo.operationId;

  if (isTrigger) {
    return {
      properties: {
        iconUri,
        brandColor,
        description,
        summary,
        settings: getSettingsForOperationType(operationType, true),
        trigger: (discoveryHints?.trigger as 'single' | 'batch') ?? 'single',
        runAfter: { type: 'notsupported' },
      },
    };
  }

  return {
    properties: {
      iconUri,
      brandColor,
      description,
      summary,
      settings: getSettingsForOperationType(operationType, false),
    },
  };
}

/**
 * Infer operation type from operation info
 */
function inferOperationType(operationInfo: OperationInfo): string {
  const { connectorId, operationId } = operationInfo;

  // Check connector ID patterns
  if (connectorId.toLowerCase().includes('http')) return 'Http';
  if (connectorId.toLowerCase().includes('agent')) return 'Agent';
  if (connectorId.toLowerCase().includes('request')) return 'Request';
  if (connectorId.toLowerCase().includes('response')) return 'Response';
  if (connectorId.toLowerCase().includes('recurrence')) return 'Recurrence';

  // Check operation ID patterns for control flow
  const opLower = operationId.toLowerCase();
  if (opLower.includes('foreach') || opLower.includes('for_each')) return 'Foreach';
  if (opLower.includes('until')) return 'Until';
  if (opLower.includes('switch')) return 'Switch';
  if (opLower.includes('condition') || opLower === 'if') return 'If';
  if (opLower.includes('scope')) return 'Scope';
  if (opLower.includes('compose')) return 'Compose';
  if (opLower.includes('parsejson') || opLower.includes('parse_json')) return 'ParseJson';
  if (opLower.includes('initializevariable')) return 'InitializeVariable';
  if (opLower.includes('setvariable')) return 'SetVariable';
  if (opLower.includes('incrementvariable')) return 'IncrementVariable';
  if (opLower.includes('appendtoarray')) return 'AppendToArrayVariable';
  if (opLower.includes('appendtostring')) return 'AppendToStringVariable';
  if (opLower.includes('wait') || opLower.includes('delay')) return 'Wait';
  if (opLower.includes('terminate')) return 'Terminate';

  // Default - return empty string to use defaults
  return '';
}

/**
 * Clear the manifest and discovery hints caches.
 * Called when switching Logic Apps so stale data doesn't persist.
 */
export function clearManifestCache(): void {
  manifestCache.clear();
  discoveryHintsCache.clear();
}

// =============================================================================
// SETTINGS AVAILABILITY
// =============================================================================

/**
 * Get available settings for an operation based on its manifest
 * Returns NodeSettings with isSupported flags set based on manifest
 */
export function getSettingsAvailability(
  manifest: OperationManifest,
  scopeOverride?: SettingScope
): NodeSettings {
  const scope = scopeOverride ?? getOperationScope(manifest);
  const manifestSettings = manifest.properties.settings ?? {};

  // For batch triggers, get all available splitOn expressions from the manifest outputs
  const isBatchTrigger = scope === 'trigger' && manifest.properties?.trigger === 'batch';
  const splitOnAllowedValues = isBatchTrigger ? getAllSplitOns(manifest) : undefined;
  const isSplitOnSupported = isBatchTrigger && !!splitOnAllowedValues?.length;

  return {
    // General settings
    timeout: createSettingData(manifestSettings.timeout, scope),
    concurrency: createSettingData(manifestSettings.concurrency, scope, {
      value: { ...DEFAULT_CONCURRENCY },
    }),
    splitOn: createSettingData(
      isSplitOnSupported ? { scopes: ['trigger'] } : undefined,
      scope,
      { value: { enabled: isSplitOnSupported, value: splitOnAllowedValues?.[0] as string } }
    ),
    splitOnAllowedValues,
    conditionExpressions: createSettingData(
      scope === 'trigger' ? { scopes: ['trigger'] } : undefined,
      scope
    ),
    count: createSettingData(manifestSettings.count, scope),

    // Networking settings
    retryPolicy: createSettingData(manifestSettings.retryPolicy, scope, {
      value: { ...DEFAULT_RETRY_POLICY },
    }),
    requestOptions: createSettingData(manifestSettings.requestOptions, scope),
    asynchronous: createAsyncSettingData(manifestSettings.operationOptions, scope, 'Asynchronous'),
    disableAsyncPattern: createAsyncSettingData(manifestSettings.operationOptions, scope, 'DisableAsyncPattern'),
    paging: createSettingData(manifestSettings.paging, scope),
    uploadChunk: createSettingData(manifestSettings.chunking, scope),
    downloadChunkSize: createSettingData(manifestSettings.downloadChunking, scope),
    suppressWorkflowHeaders: createAsyncSettingData(
      manifestSettings.operationOptions,
      scope,
      'SuppressWorkflowHeaders'
    ),
    suppressWorkflowHeadersOnResponse: createAsyncSettingData(
      manifestSettings.operationOptions,
      scope,
      'SuppressWorkflowHeadersOnResponse'
    ),

    // Security settings
    secureInputs: createSettingData(manifestSettings.secureData, scope),
    secureOutputs: createSettingData(manifestSettings.secureData, scope),

    // Tracking settings
    trackedProperties: createSettingData(manifestSettings.trackedProperties, scope),
    correlation: createSettingData(manifestSettings.correlation, scope),

    // Run after (always supported for actions)
    runAfter: {
      isSupported: scope === 'action' && manifest.properties.runAfter?.type !== 'notsupported',
    },

    // Data handling
    requestSchemaValidation: createAsyncSettingData(
      manifestSettings.operationOptions,
      scope,
      'EnableSchemaValidation'
    ),
    disableAutomaticDecompression: createAsyncSettingData(
      manifestSettings.operationOptions,
      scope,
      'DisableAutomaticDecompression'
    ),

    // Connection settings
    invokerConnection: createSettingData(manifestSettings.invokerConnection, scope),
  };
}

/**
 * Create a SettingData object based on manifest setting and scope
 */
function createSettingData<T>(
  manifestSetting: { scopes?: SettingScope[] } | undefined,
  scope: SettingScope,
  defaults?: { value?: T }
): SettingData<T> {
  const isSupported = isSettingSupported(manifestSetting, scope);

  if (!isSupported) {
    return { isSupported: false };
  }

  if (defaults?.value !== undefined) {
    return { isSupported: true, value: defaults.value };
  }

  return { isSupported: true };
}

/**
 * Create a SettingData for operation options (async pattern, decompression, etc.)
 */
function createAsyncSettingData(
  operationOptions: OperationManifestSettings['operationOptions'],
  scope: SettingScope,
  optionName: string
): SettingData<boolean> {
  if (!operationOptions) {
    return { isSupported: false };
  }

  if (!isSettingSupported(operationOptions, scope)) {
    return { isSupported: false };
  }

  const options = operationOptions.options ?? [];
  const isSupported = options.includes(optionName as never);

  return { isSupported };
}

// =============================================================================
// PARAMETER PARSING
// =============================================================================

/**
 * Parse manifest inputs schema to ParameterInfo array
 */
export function getParameters(manifest: OperationManifest): ParameterInfo[] {
  const inputsSchema = manifest.properties.inputs;

  if (!inputsSchema) {
    return [];
  }

  // Check if inputs has properties (standard structure) or is a single parameter schema
  // Manifests like condition/switch define inputs as a single parameter without nested properties
  if (inputsSchema.type === 'object' && inputsSchema.properties) {
    // Standard case: parse nested properties
    return parseSchemaToParameters(inputsSchema, '', '');
  }

  // Single parameter case: the entire inputs schema is one parameter
  // Use inputsLocation to determine the parameter name (e.g., ['expression'] -> 'expression')
  const inputsLocation = manifest.properties.inputsLocation;
  let paramName = 'inputs';
  if (inputsLocation && inputsLocation.length > 0) {
    const lastSegment = inputsLocation[inputsLocation.length - 1];
    if (lastSegment) {
      paramName = lastSegment;
    }
  }

  // Treat the entire inputs schema as a single parameter
  const param = schemaPropertyToParameter(
    paramName,
    inputsSchema as SchemaObject,
    paramName,
    paramName,
    inputsSchema.required === true
  );

  // If the manifest declares inputs are not optional, mark the parameter as required
  if (manifest.properties.isInputsOptional === false && !param.required) {
    param.required = true;
  }

  return [param];
}

/**
 * Get parameters grouped by visibility
 */
export function getParameterGroups(manifest: OperationManifest): ParameterGroup[] {
  const parameters = getParameters(manifest);

  const basicParams = parameters.filter(
    (p) => p.visibility !== ParameterVisibility.Advanced && p.visibility !== ParameterVisibility.Internal
  );
  const advancedParams = parameters.filter(
    (p) => p.visibility === ParameterVisibility.Advanced
  );

  const groups: ParameterGroup[] = [];

  if (basicParams.length > 0) {
    groups.push({
      id: 'basic',
      name: 'Basic',
      parameters: basicParams,
    });
  }

  if (advancedParams.length > 0) {
    groups.push({
      id: 'advanced',
      name: 'Advanced',
      description: 'Additional configuration options',
      parameters: advancedParams,
      collapsedByDefault: true,
    });
  }

  return groups;
}

/**
 * Parse a JSON schema to parameter definitions
 * Recursively flattens nested object properties into dot-notation parameter keys
 */
function parseSchemaToParameters(
  schema: SchemaObject,
  path: string,
  parentKey: string
): ParameterInfo[] {
  const parameters: ParameterInfo[] = [];

  if (schema.type === 'object' && schema.properties) {
    for (const [propName, propSchema] of Object.entries(schema.properties)) {
      const propPath = path ? `${path}.${propName}` : propName;
      const paramKey = parentKey ? `${parentKey}.${propName}` : propName;
      const propSchemaTyped = propSchema as SchemaObject;

      // Check if this is a nested object that should be flattened.
      // Default behavior: flatten nested objects so each child renders as its own editor
      // (matches LogicAppsUX semantics, e.g. Service Bus `message` parameter).
      // Do NOT flatten when:
      //   - The property has an explicit `x-ms-editor` (e.g. `dictionary`, `code`) — author
      //     intentionally chose a single composite editor for the whole object.
      //   - The property is a true open-ended map: `additionalProperties: true` or
      //     `additionalProperties: { ...schema... }`. `additionalProperties: false` only
      //     forbids extra keys on a fixed-shape object — it does NOT make it a dictionary,
      //     so it must still flatten.
      const isNestedObject = propSchemaTyped.type === 'object' && propSchemaTyped.properties;
      const hasExplicitEditor = propSchemaTyped['x-ms-editor'] !== undefined;
      const additionalProps = propSchemaTyped['additionalProperties'];
      const isOpenEndedMap =
        additionalProps === true ||
        (typeof additionalProps === 'object' && additionalProps !== null);
      const shouldFlatten = !hasExplicitEditor && !isOpenEndedMap;

      if (isNestedObject && shouldFlatten) {
        // Recursively flatten nested object properties
        const nestedParams = parseSchemaToParameters(
          propSchemaTyped,
          propPath,
          paramKey
        );
        // Compute the parent label once so children can render under a collapsible
        // group container in the parameter list.
        const parentLabel = (propSchemaTyped.title as string | undefined) ?? formatLabel(propName);
        const parentGroupInfo = { key: paramKey, name: propName, label: parentLabel };
        // Inherit visibility from parent if children don't have explicit visibility,
        // and tag each child with its immediate parent group (only the first nested
        // ancestor — deeper levels keep the closest enclosing group).
        nestedParams.forEach(p => {
          if (!p.visibility || p.visibility === ParameterVisibility.Default) {
            if (propSchemaTyped['x-ms-visibility'] === 'advanced') {
              p.visibility = ParameterVisibility.Advanced;
            }
          }
          if (p.info && p.info.parentProperty === undefined) {
            p.info.parentProperty = parentGroupInfo;
          }
        });
        parameters.push(...nestedParams);
      } else {
        // Add as a single parameter
        const param = schemaPropertyToParameter(
          propName,
          propSchemaTyped,
          propPath,
          paramKey,
          isPropertyRequired(schema, propName, propSchemaTyped)
        );
        parameters.push(param);
      }
    }
  }

  return parameters;
}

/**
 * Shared envelope decoder for `x-ms-dynamic-properties`. Used both for child
 * parameters (in `schemaPropertyToParameter`) and for the inputs-root extension
 * surfaced via `getInputsDynamicSchema` (used by literal-type built-ins such as
 * ChunkText where the dynamic call lives on the inputs object itself rather
 * than on one of its properties).
 *
 *   "x-ms-dynamic-properties": {
 *     "dynamicState": { "extension": { "operationId": "...", "parameters": {} } },
 *     "parameters": { "<paramName>": { "parameterReference": "...", "required": true } }
 *   }
 *
 * `builtInOperation` (client-side resolver, e.g. variables.ts getVariable) is
 * intentionally ignored — that needs a separate in-process resolver registry
 * and isn't a parsing failure.
 */
type DynamicPropertiesShape = {
  operationId?: string;
  parameters?: Record<string, { parameter?: string; parameterReference?: string; value?: unknown; required?: boolean }>;
  'value-path'?: string;
  dynamicState?: {
    extension?: {
      operationId?: string;
      builtInOperation?: string;
      parameters?: Record<string, unknown>;
    };
    operationId?: string;
    builtInOperation?: string;
    isInput?: boolean;
  };
};

function extractDynamicPropertiesConfig(
  rawExt: unknown,
  paramNameForWarn?: string,
): DynamicSchemaConfig | undefined {
  if (!rawExt || typeof rawExt !== 'object') return undefined;
  const raw = rawExt as DynamicPropertiesShape;
  const opId = raw.dynamicState?.extension?.operationId
    ?? raw.dynamicState?.operationId
    ?? raw.operationId;
  const builtInOp = raw.dynamicState?.extension?.builtInOperation
    ?? raw.dynamicState?.builtInOperation;
  if (opId) {
    const dpConfig: DynamicSchemaConfig = { operationId: opId };
    if (raw.parameters) dpConfig.parameters = raw.parameters;
    if (raw['value-path']) dpConfig.valuePath = raw['value-path'];
    return dpConfig;
  }
  if (!builtInOp) {
    console.warn('[DynamicProperties] x-ms-dynamic-properties found but no operationId extracted', {
      paramName: paramNameForWarn,
      rawShape: raw,
    });
  }
  return undefined;
}

/**
 * Read root-level `x-ms-dynamic-properties` off the manifest's inputs schema.
 *
 * Used by runtime literal-type built-ins (e.g. ChunkText, Xslt) whose inputs
 * object itself carries the dynamic extension — fetching the schema injects
 * extra siblings to the static `properties` (e.g. EncodingModel, maxPageLength
 * for ChunkText/TokenSize). Distinct from per-property `dynamicSchema` which
 * is handled inline during parameter parsing.
 */
export function getInputsDynamicSchema(manifest: OperationManifest): DynamicSchemaConfig | undefined {
  const inputsSchema = manifest.properties.inputs as SchemaObject | undefined;
  if (!inputsSchema) return undefined;
  return extractDynamicPropertiesConfig(
    (inputsSchema as Record<string, unknown>)['x-ms-dynamic-properties'],
    '__inputs__',
  );
}

/**
 * Convert a schema property to a ParameterInfo
 */
function schemaPropertyToParameter(
  name: string,
  schema: SchemaObject,
  _path: string,
  key: string,
  required: boolean
): ParameterInfo {
  const paramType = schemaTypeToParameterType(schema.type);
  const editor = inferEditor(schema, paramType);
  const { visibility, hideInUI } = inferVisibility(name, schema);

  // Build info object, only including format if it exists
  const info: ParameterInfo['info'] = {};
  if (schema.format) {
    info.format = schema.format;
  }

  // Extract x-ms-serialization metadata so consumers (e.g. buildInputsFromParameters)
  // can honor `skip: true` for fields that must never be written to the workflow
  // definition (display-only values like Request.callbackUrl, or HTTP swagger operationId
  // that is rebuilt from operationDetails on serialization).
  const rawSerialization = (schema as Record<string, unknown>)['x-ms-serialization'] as
    | { skip?: boolean; property?: { type: string; name?: string; parameterReference?: string }; format?: string }
    | undefined;
  if (rawSerialization && typeof rawSerialization === 'object') {
    const serialization: NonNullable<ParameterInfo['info']['serialization']> = {};
    if (rawSerialization.skip !== undefined) serialization.skip = rawSerialization.skip;
    if (rawSerialization.property) {
      const prop = rawSerialization.property;
      const serProp: NonNullable<NonNullable<ParameterInfo['info']['serialization']>['property']> = {
        name: prop.name ?? name,
        type: prop.type,
      };
      if (prop.parameterReference !== undefined) serProp.parameterReference = prop.parameterReference;
      serialization.property = serProp;
    }
    if (rawSerialization.format) serialization.format = rawSerialization.format;
    if (Object.keys(serialization).length > 0) {
      info.serialization = serialization;
    }
  }

  // Extract x-ms-dynamic-list (modern) or x-ms-dynamic-values (legacy) for dynamic dropdowns
  // Service provider / built-in operation manifests wrap the invocation under `dynamicState`:
  //   "x-ms-dynamic-list": { "dynamicState": { "operationId": "getEntities", "parameters": {} }, "parameters": { ... } }
  // Managed-API swagger uses the flat shape:
  //   "x-ms-dynamic-list": { "operationId": "...", "parameters": { ... }, "itemValuePath": "..." }
  // We accept both by promoting `dynamicState.operationId` to the top level when present.
  type DynamicListShape = {
    operationId?: string;
    parameters?: Record<string, { parameterReference?: string; value?: unknown; required?: boolean }>;
    itemsPath?: string;
    itemValuePath?: string;
    itemTitlePath?: string;
    itemDescriptionPath?: string;
    itemSelectablePath?: string;
    isLegacy?: boolean; // Preserved flag to indicate original extension type
    dynamicState?: {
      operationId?: string;
      parameters?: Record<string, { parameterReference?: string; value?: unknown; required?: boolean }>;
      itemsPath?: string;
      itemValuePath?: string;
      itemTitlePath?: string;
      itemDescriptionPath?: string;
      itemSelectablePath?: string;
    };
  };
  const rawDynamicList = schema['x-ms-dynamic-list'] as DynamicListShape | undefined;
  // Built-in / service-provider manifests wrap the runtime invocation under `dynamicState`,
  // while managed-API swagger uses the flat shape. Normalize to flat — explicit top-level
  // keys win over `dynamicState` siblings (e.g. top-level `parameters` carries the editor's
  // parameter-reference map, while `dynamicState.parameters` is the runtime invocation payload).
  const { dynamicState: dlState, ...dlTop } = rawDynamicList ?? {};
  const dynamicList: DynamicListShape | undefined = rawDynamicList
    ? (dlState ? { ...dlState, ...dlTop } : rawDynamicList)
    : undefined;

  const dynamicValues = (schema as Record<string, unknown>)['x-ms-dynamic-values'] as {
    operationId?: string;
    parameters?: Record<string, string | { parameter?: string; value?: unknown }>;
    'value-collection'?: string;
    'value-path'?: string;
    'value-title'?: string;
  } | undefined;

  if (dynamicList?.operationId) {
    // x-ms-dynamic-list - may be modern or preserved from legacy x-ms-dynamic-values
    const dvConfig: typeof info.dynamicValues = {
      operationId: dynamicList.operationId,
      isLegacy: dynamicList.isLegacy ?? false, // Use preserved value or default to false
    };
    if (dynamicList.parameters) dvConfig.parameters = dynamicList.parameters;
    if (dynamicList.itemsPath) dvConfig.itemsPath = dynamicList.itemsPath;
    if (dynamicList.itemValuePath) dvConfig.itemValuePath = dynamicList.itemValuePath;
    if (dynamicList.itemTitlePath) dvConfig.itemTitlePath = dynamicList.itemTitlePath;
    if (dynamicList.itemDescriptionPath) dvConfig.itemDescriptionPath = dynamicList.itemDescriptionPath;
    if (dynamicList.itemSelectablePath) dvConfig.itemSelectablePath = dynamicList.itemSelectablePath;
    info.dynamicValues = dvConfig;
  } else if (rawDynamicList && !dynamicList?.operationId) {
    // Diagnostic: x-ms-dynamic-list extension exists on the schema but we couldn't
    // extract an operationId. This usually means the manifest uses a shape we don't
    // recognise yet (neither flat `operationId` nor wrapped `dynamicState.operationId`).
    console.warn('[DynamicList] x-ms-dynamic-list found but no operationId extracted', {
      paramName: name,
      rawShape: rawDynamicList,
    });
  }
  if (!dynamicList?.operationId && dynamicValues?.operationId) {
    // Legacy x-ms-dynamic-values - uses /dynamicInvoke endpoint with path-based request
    const dvConfig: typeof info.dynamicValues = {
      operationId: dynamicValues.operationId,
      isLegacy: true, // Legacy extension uses /dynamicInvoke endpoint
    };
    // Convert legacy parameter format
    // In x-ms-dynamic-values:
    //   - Plain string like "ParentMessage" = static value
    //   - Object with { parameter: "foo" } = reference to parameter "foo"
    if (dynamicValues.parameters) {
      const params: Record<string, { parameterReference?: string; value?: unknown }> = {};
      for (const [key, paramValue] of Object.entries(dynamicValues.parameters)) {
        if (typeof paramValue === 'string') {
          // Plain string = static value (NOT a parameter reference)
          params[key] = { value: paramValue };
        } else if (paramValue && typeof paramValue === 'object') {
          // Object format with parameter/value
          const paramObj = paramValue as { parameter?: string; value?: unknown };
          const paramConfig: { parameterReference?: string; value?: unknown } = {};
          if (paramObj.parameter !== undefined) {
            paramConfig.parameterReference = paramObj.parameter;
          }
          if (paramObj.value !== undefined) {
            paramConfig.value = paramObj.value;
          }
          params[key] = paramConfig;
        }
      }
      dvConfig.parameters = params;
    }
    // Map legacy property names to modern format
    if (dynamicValues['value-collection']) dvConfig.itemsPath = dynamicValues['value-collection'];
    if (dynamicValues['value-path']) dvConfig.itemValuePath = dynamicValues['value-path'];
    if (dynamicValues['value-title']) dvConfig.itemTitlePath = dynamicValues['value-title'];
    info.dynamicValues = dvConfig;
  }

  // Extract x-ms-input-dependencies for conditional visibility
  const inputDeps = schema['x-ms-input-dependencies'];
  if (inputDeps && typeof inputDeps === 'object') {
    const dependencies = parseInputDependencies(inputDeps);
    if (dependencies.length > 0) {
      info.inputDependencies = dependencies;
    }
  }

  // Extract x-ms-dynamic-schema for dynamic field generation.
  // Same wrapping convention as x-ms-dynamic-list: managed-API swagger uses the flat shape,
  // service-provider manifests may wrap the invocation under `dynamicState`. Normalize
  // identically so the consumer sees a flat `{ operationId, parameters, value-path }`.
  type DynamicSchemaShape = {
    operationId?: string;
    parameters?: Record<string, { parameter?: string; parameterReference?: string; value?: unknown }>;
    'value-path'?: string;
    dynamicState?: {
      operationId?: string;
      parameters?: Record<string, { parameter?: string; parameterReference?: string; value?: unknown }>;
      'value-path'?: string;
    };
  };
  const rawDynamicSchema = schema['x-ms-dynamic-schema'] as DynamicSchemaShape | undefined;
  const { dynamicState: dsState, ...dsTop } = rawDynamicSchema ?? {};
  const dynamicSchema: DynamicSchemaShape | undefined = rawDynamicSchema
    ? (dsState ? { ...dsState, ...dsTop } : rawDynamicSchema)
    : undefined;

  if (dynamicSchema?.operationId) {
    const dsConfig: typeof info.dynamicSchema = {
      operationId: dynamicSchema.operationId,
    };
    if (dynamicSchema.parameters) dsConfig.parameters = dynamicSchema.parameters;
    if (dynamicSchema['value-path']) dsConfig.valuePath = dynamicSchema['value-path'];
    info.dynamicSchema = dsConfig;
  }

  if (!info.dynamicSchema) {
    const dpConfig = extractDynamicPropertiesConfig(schema['x-ms-dynamic-properties'], name);
    if (dpConfig) info.dynamicSchema = dpConfig;
  }

  // Extract x-ms-dynamic-tree for file/folder pickers
  const dynamicTree = schema['x-ms-dynamic-tree'];
  if (dynamicTree && typeof dynamicTree === 'object' && Object.keys(dynamicTree as object).length > 0) {
    info.dynamicTree = dynamicTree as NonNullable<typeof info.dynamicTree>;
  }

  // Restore body sub-path written by parametersToSchema so dynamic-schema
  // children resolved via useDynamicSchema serialize at the right body location
  // ('' for SharePoint PostItem body-root, '<propName>' for SQL ExecuteProcedure_V2).
  if (typeof schema['x-otto-body-key-path'] === 'string') {
    info.bodyKeyPath = schema['x-otto-body-key-path'];
  }

  // Restore body-root marker so primitive-body fields display the value
  // stored under the literal `body` key on first render.
  if (schema['x-otto-body-root'] === true) {
    info.isBodyRoot = true;
  }

  // Extract editor options from x-ms-editor-options
  const xmsEditorOptions = schema['x-ms-editor-options'] as Record<string, unknown> | undefined;

  // Build the result object
  const result: ParameterInfo = {
    id: `param_${key}`,
    parameterKey: key,
    parameterName: name,
    label: schema.title ?? formatLabel(name),
    type: paramType,
    required,
    editor,
    visibility,
    hideInUI,
    schema,
    value: [],
    info,
    dynamicData: {
      status: DynamicLoadStatus.NotStarted,
    },
    showTokens: true,
    showErrors: true,
  };

  // Only add editorOptions if defined
  if (xmsEditorOptions) {
    result.editorOptions = xmsEditorOptions;
  }

  // Set default value if there's one in the schema
  if (schema.default !== undefined) {
    result.defaultValue = schema.default;
    result.placeholder = String(schema.default);
  }

  return result;
}

/**
 * Infer the best editor for a parameter
 * Priority: x-ms-editor > x-ms-dynamic-list > x-ms-dynamic-tree > enum > format > type
 */
function inferEditor(schema: SchemaObject, paramType: ParameterType): ParameterEditor {
  // First check for explicit x-ms-editor in schema
  const xmsEditor = schema['x-ms-editor'] as string | undefined;
  if (xmsEditor) {
    // Special case: 'html' editor -> Html editor
    if (xmsEditor.toLowerCase() === 'html') {
      return ParameterEditor.Html;
    }
    // Special case: 'filepicker' editor -> FilePicker editor
    if (xmsEditor.toLowerCase() === 'filepicker') {
      return ParameterEditor.FilePicker;
    }
    // Special case: 'dynamiccombobox' editor -> DynamicCombobox editor
    if (xmsEditor.toLowerCase() === 'dynamiccombobox') {
      return ParameterEditor.DynamicCombobox;
    }
    // Special case: 'readonlyexternal' editor -> ReadOnlyExternal editor
    if (xmsEditor.toLowerCase() === 'readonlyexternal') {
      return ParameterEditor.ReadOnlyExternal;
    }
    const mappedEditor = mapXmsEditorToParameterEditor(xmsEditor);
    if (mappedEditor) {
      return mappedEditor;
    }
  }

  // Check for x-ms-dynamic-list -> DynamicCombobox
  // Service-provider/built-in manifests wrap operationId under `dynamicState`, while
  // managed-API swagger uses the flat shape. Accept both — must match the parser in
  // schemaPropertyToParameter().
  const dynamicList = schema['x-ms-dynamic-list'] as
    | { operationId?: string; dynamicState?: { operationId?: string } }
    | undefined;
  if (dynamicList?.operationId || dynamicList?.dynamicState?.operationId) {
    return ParameterEditor.DynamicCombobox;
  }

  // Check for x-ms-dynamic-tree -> FilePicker
  const dynamicTree = schema['x-ms-dynamic-tree'] as object | undefined;
  if (dynamicTree && typeof dynamicTree === 'object' && Object.keys(dynamicTree).length > 0) {
    return ParameterEditor.FilePicker;
  }

  // Check for enum -> dropdown
  if (schema.enum && schema.enum.length > 0) {
    return ParameterEditor.Combobox;
  }

  // Check for x-ms-editor-options with options array (implies combobox)
  const editorOptions = schema['x-ms-editor-options'] as { options?: unknown[] } | undefined;
  if (editorOptions?.options && Array.isArray(editorOptions.options) && editorOptions.options.length > 0) {
    return ParameterEditor.Combobox;
  }

  // Check format hints
  if (schema.format === 'date-time') {
    return ParameterEditor.DateTime;
  }

  // Check for multiline hint in description
  if (schema.description?.toLowerCase().includes('multiline')) {
    return ParameterEditor.TextArea;
  }

  // Infer from type
  switch (paramType) {
    case ParameterType.Boolean:
      return ParameterEditor.Boolean;
    case ParameterType.Array:
      return ParameterEditor.Array;
    case ParameterType.Object: {
      // Only treat as Dictionary when the schema explicitly opts into an
      // open-ended map (`additionalProperties: true | { ... }`). Plain
      // `{ type: 'object' }` (no `properties`, no `additionalProperties`)
      // means "any value" — fall back to the tokenized String editor so
      // authors can paste expressions / JSON instead of being forced into
      // a key/value grid. Schemas with `properties` are flattened upstream
      // and don't reach this branch.
      const additional = (schema as { additionalProperties?: unknown }).additionalProperties;
      const isOpenEndedMap =
        additional === true ||
        (typeof additional === 'object' && additional !== null);
      return isOpenEndedMap ? ParameterEditor.Dictionary : ParameterEditor.String;
    }
    default:
      return ParameterEditor.String;
  }
}

/**
 * Infer visibility from parameter name and schema
 * Returns both visibility level and hideInUI flag
 */
function inferVisibility(name: string, schema: SchemaObject): { visibility: ParameterVisibility; hideInUI: boolean } {
  // Check for explicit visibility in schema extensions
  if (schema['x-ms-visibility']) {
    const vis = schema['x-ms-visibility'] as string;
    if (vis === 'hideInUI') return { visibility: ParameterVisibility.Internal, hideInUI: true };
    if (vis === 'advanced') return { visibility: ParameterVisibility.Advanced, hideInUI: false };
    if (vis === 'internal') return { visibility: ParameterVisibility.Internal, hideInUI: false };
    if (vis === 'important') return { visibility: ParameterVisibility.Important, hideInUI: false };
  }

  // Common advanced parameter patterns
  const advancedPatterns = [
    'timeout',
    'retry',
    'headers',
    'authentication',
    'options',
    'advanced',
    'metadata',
  ];

  const lowerName = name.toLowerCase();
  if (advancedPatterns.some((pattern) => lowerName.includes(pattern))) {
    return { visibility: ParameterVisibility.Advanced, hideInUI: false };
  }

  return { visibility: ParameterVisibility.Default, hideInUI: false };
}

/**
 * Format a parameter name as a display label
 */
function formatLabel(name: string): string {
  // Convert camelCase to Title Case with spaces
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

// =============================================================================
// CONNECTION REQUIREMENTS
// =============================================================================

/**
 * Get connection requirements from manifest
 */
export function getConnectionRequirements(manifest: OperationManifest): {
  required: boolean;
  type?: string;
} | null {
  const connection = manifest.properties.connection;

  if (!connection) {
    return null;
  }

  const result: { required: boolean; type?: string } = {
    required: connection.required,
  };

  if (connection.type) {
    result.type = connection.type;
  }

  return result;
}

// =============================================================================
// MANIFEST UTILITIES
// =============================================================================

/**
 * Check if operation is a trigger
 */
export function isTriggerOperation(manifest: OperationManifest): boolean {
  return !!manifest.properties.trigger;
}

/**
 * Get operation description
 */
export function getOperationDescription(manifest: OperationManifest): string {
  return manifest.properties.description ?? manifest.properties.summary ?? '';
}

/**
 * Get external documentation URL
 */
export function getDocumentationUrl(manifest: OperationManifest): string | undefined {
  return manifest.properties.externalDocs?.url;
}

// =============================================================================
// SWAGGER TO MANIFEST CONVERSION
// =============================================================================

/**
 * Convert a swagger operation to an OperationManifest
 * Uses SwaggerParser for proper parameter extraction and visibility handling
 */
export function swaggerOperationToManifest(
  swagger: SwaggerSpec,
  operationId: string
): OperationManifest | null {
  const parser = new SwaggerParser(swagger);
  const parsedOperation = parser.getOperationByOperationId(operationId);

  if (!parsedOperation) {
    return null;
  }

  const { operation } = parsedOperation;
  const isTrigger = parser.isTriggerOperation(operationId);

  // Get input parameters using the parser (handles $ref resolution, visibility filtering, sorting)
  const inputParameters = parser.getInputParameters(operationId, {
    excludeInternalParameters: true,
    excludeAdvancedParameters: false,
  });

  // Get output parameters from the response schema
  const outputParameters = parser.getOutputParameters(operationId, {
    excludeInternalOutputs: true,
    excludeAdvancedOutputs: false,
  });

  // Convert parameters to schema format for the manifest
  const inputsSchema = parametersToSchema(inputParameters);
  const outputsSchema = outputParametersToSchema(outputParameters);

  // Preserve definitions from swagger for $ref resolution in complex array items
  if (swagger.definitions) {
    inputsSchema.definitions = swagger.definitions as Record<string, SchemaObject>;
  }

  const manifestProperties: OperationManifest['properties'] = {
    iconUri: '',
    brandColor: '#0078D4',
    description: operation.description ?? '',
    summary: operation.summary ?? operationId,
    settings: isTrigger ? {
      concurrency: { scopes: ['trigger'] },
      correlation: { scopes: ['trigger'] },
      secureData: {},
      trackedProperties: {},
    } : {
      timeout: { scopes: ['action'] },
      retryPolicy: { scopes: ['action'] },
      concurrency: { scopes: ['action'] },
      secureData: {},
      trackedProperties: {},
    },
    inputs: inputsSchema,
    outputs: outputsSchema,
  };

  // Only add externalDocs if it exists
  if (operation.externalDocs) {
    manifestProperties.externalDocs = {
      url: operation.externalDocs.url,
    };
  }

  const manifest: OperationManifest = {
    properties: manifestProperties,
  };

  if (isTrigger) {
    manifest.properties.trigger = operation['x-ms-trigger'] === 'batch' ? 'batch' : 'single';
  }

  // Managed connectors require connections
  manifest.properties.connection = {
    required: true,
  };

  return manifest;
}

/**
 * Convert a swagger operation to an OperationManifest using path and method.
 * Useful for legacy tools that don't have operationId stored.
 */
export function swaggerOperationToManifestByPath(
  swagger: SwaggerSpec,
  path: string,
  method: string
): OperationManifest | null {
  const parser = new SwaggerParser(swagger);
  const parsedOperation = parser.getOperationByPathAndMethod(path, method);

  if (!parsedOperation) {
    return null;
  }

  // Use the found operationId with the standard function
  return swaggerOperationToManifest(swagger, parsedOperation.operationId);
}

/**
 * Convert ParameterInfo array to a schema object for the manifest inputs
 */
function parametersToSchema(parameters: ParameterInfo[]): SchemaObject {
  const properties: Record<string, SchemaObject> = {};
  const required: string[] = [];

  for (const param of parameters) {
    const schema: SchemaObject = {
      type: param.schema?.type ?? 'string',
      title: param.label,
    };

    if (param.schema?.description) {
      schema.description = param.schema.description;
    }
    if (param.schema?.format) {
      schema.format = param.schema.format;
    }
    if (param.schema?.enum) {
      schema.enum = param.schema.enum;
    }
    // Preserve the typed default (array/boolean/object) for round-trip parity.
    // `param.placeholder` is the stringified UI hint and would collapse
    // arrays/booleans into strings.
    if (param.defaultValue !== undefined) {
      schema.default = param.defaultValue;
    }

    // Preserve items for array types (needed for complex array item editing)
    if (param.schema?.items) {
      schema.items = param.schema.items;
    }

    // Preserve nested properties for object types
    if (param.schema?.properties) {
      schema.properties = param.schema.properties;
    }

    // Preserve required array for nested objects
    if (param.schema?.required && Array.isArray(param.schema.required)) {
      schema.required = param.schema.required;
    }

    // Map visibility back to x-ms-visibility.
    // `hideInUI` wins over advanced/important so dynamic-schema container parents
    // (set hidden by SwaggerParser) survive the parametersToSchema → schemaPropertyToParameter
    // round-trip and the renderer filters them out.
    if (param.hideInUI) {
      schema['x-ms-visibility'] = 'hideInUI';
    } else if (param.visibility === ParameterVisibility.Advanced) {
      schema['x-ms-visibility'] = 'advanced';
    } else if (param.visibility === ParameterVisibility.Important) {
      schema['x-ms-visibility'] = 'important';
    }

    // Preserve body sub-path for dynamic-schema parents so the renderer knows
    // where to nest resolved children (SQL ExecuteProcedure_V2: 'parameters',
    // SharePoint PostItem: ''). Lost otherwise because `info` is not serialized.
    if (param.info?.bodyKeyPath !== undefined) {
      schema['x-otto-body-key-path'] = param.info.bodyKeyPath;
    }

    // Preserve body-root marker so the editor field can fall back to
    // `parameterValues['body']` for primitive bodies (Document Intelligence
    // `inputFileContent`, etc.) — see `findRootBodyParam`.
    if (param.info?.isBodyRoot) {
      schema['x-otto-body-root'] = true;
    }

    // Preserve dynamic values configuration (x-ms-dynamic-list / x-ms-dynamic-values)
    if (param.info?.dynamicValues) {
      const dv = param.info.dynamicValues;
      // Store as x-ms-dynamic-list format (modern format) but preserve isLegacy flag
      schema['x-ms-dynamic-list'] = {
        operationId: dv.operationId,
        parameters: dv.parameters,
        itemsPath: dv.itemsPath,
        itemValuePath: dv.itemValuePath,
        itemTitlePath: dv.itemTitlePath,
        // Preserve the isLegacy flag so we know which endpoint to use
        ...(dv.isLegacy !== undefined && { isLegacy: dv.isLegacy }),
      };
    }

    // Preserve input dependencies (x-ms-input-dependencies)
    if (param.info?.inputDependencies && param.info.inputDependencies.length > 0) {
      const deps: Record<string, { type: string; values?: unknown[]; excludeValues?: unknown[] }> = {};
      for (const dep of param.info.inputDependencies) {
        deps[dep.parameterReference] = {
          type: dep.type,
          ...(dep.values && { values: dep.values }),
          ...(dep.excludeValues && { excludeValues: dep.excludeValues }),
        };
      }
      schema['x-ms-input-dependencies'] = deps;
    }

    // Preserve dynamic schema configuration (x-ms-dynamic-schema)
    if (param.info?.dynamicSchema) {
      const ds = param.info.dynamicSchema;
      schema['x-ms-dynamic-schema'] = {
        operationId: ds.operationId,
        parameters: ds.parameters,
        'value-path': ds.valuePath,
      };
    }

    // Preserve dynamic tree configuration (x-ms-dynamic-tree)
    if (param.info?.dynamicTree) {
      schema['x-ms-dynamic-tree'] = param.info.dynamicTree;
    }

    // Preserve editor type (x-ms-editor)
    if (param.editor) {
      schema['x-ms-editor'] = param.editor;
    }

    // Preserve editor options (x-ms-editor-options)
    if (param.editorOptions) {
      schema['x-ms-editor-options'] = param.editorOptions;
    }

    properties[param.parameterKey] = schema;

    if (param.required) {
      required.push(param.parameterKey);
    }
  }

  const result: SchemaObject = {
    type: 'object',
    properties,
  };

  if (required.length > 0) {
    result.required = required;
  }

  return result;
}

/**
 * Convert output ParameterInfo array to a schema object for the manifest outputs.
 * Wraps outputs in a body property structure like LogicAppsUX manifests.
 * Recursively includes nested properties from parameter schemas.
 */
function outputParametersToSchema(parameters: ParameterInfo[]): SchemaObject {
  // When the parser emitted a single ParameterInfo named 'body' whose schema
  // already represents the entire response body, use it directly to avoid
  // double-wrapping as body.properties.body. This happens when the Swagger
  // response is a top-level array, a simple/primitive type, or an object
  // without explicit `properties` (e.g. only `additionalProperties` and/or
  // `x-ms-dynamic-schema`, as in SQL ExecuteProcedure_V2). Without this,
  // generateTokensFromSchema would synthesise a bogus nested `['body']`
  // token, e.g. ${body('Execute_stored_procedure_(V2)')['body']}.
  if (parameters.length === 1 && parameters[0]!.parameterName === 'body') {
    const param = parameters[0]!;
    const paramSchema = param.schema;
    const isWholeBody =
      !paramSchema ||
      paramSchema.type !== 'object' ||
      !paramSchema.properties;
    if (isWholeBody) {
      const bodySchema: SchemaObject = paramSchema ? { ...paramSchema } : { type: 'object' };
      if (!bodySchema.title) {
        bodySchema.title = 'Body';
      }
      return {
        type: 'object',
        properties: {
          body: bodySchema,
        },
      };
    }
  }

  const bodyProperties: Record<string, SchemaObject> = {};

  for (const param of parameters) {
    // Use the full schema if available (includes nested properties)
    const schema: SchemaObject = param.schema ? { ...param.schema } : {
      type: 'string',
    };

    // Ensure title is set
    if (!schema.title) {
      schema.title = param.label;
    }

    // Map visibility back to x-ms-visibility
    if (param.visibility === ParameterVisibility.Advanced) {
      schema['x-ms-visibility'] = 'advanced';
    } else if (param.visibility === ParameterVisibility.Important) {
      schema['x-ms-visibility'] = 'important';
    }

    bodyProperties[param.parameterName] = schema;
  }

  // Wrap in the standard output structure: { type: 'object', properties: { body: { ... } } }
  return {
    type: 'object',
    properties: {
      body: {
        type: 'object',
        title: 'Body',
        properties: bodyProperties,
      },
    },
  };
}

/**
 * Get input parameters from swagger for a specific operation
 * Uses SwaggerParser for proper visibility filtering and parameter sorting
 */
export function getParametersFromSwagger(
  swagger: SwaggerSpec,
  operationId: string
): ParameterInfo[] {
  const parser = new SwaggerParser(swagger);
  return parser.getInputParameters(operationId, {
    excludeInternalParameters: true,
    excludeAdvancedParameters: false,
  });
}

/**
 * Get output parameters from swagger for a specific operation
 * Uses SwaggerParser for proper visibility filtering and parameter sorting
 */
export function getOutputParametersFromSwagger(
  swagger: SwaggerSpec,
  operationId: string
): ParameterInfo[] {
  const parser = new SwaggerParser(swagger);
  return parser.getOutputParameters(operationId, {
    excludeInternalOutputs: true,
    excludeAdvancedOutputs: false,
  });
}

// =============================================================================
// SERVICE PROVIDER MANIFEST CONVERSION
// =============================================================================

/**
 * Service Provider Operation response type (imported from logicAppsApi)
 * This is a minimal type definition - the full type is in logicAppsApi.ts
 */
interface ServiceProviderOperationManifest {
  inputs?: {
    type: string;
    properties?: Record<string, unknown>;
    required?: string[];
  };
  outputs?: {
    type: string;
    properties?: Record<string, unknown>;
  };
  settings?: {
    secureData?: Record<string, unknown>;
    trackedProperties?: { scopes?: string[] };
    retryPolicy?: { scopes?: string[] };
    timeout?: { scopes?: string[] };
    concurrency?: { scopes?: string[] };
  };
}

interface ServiceProviderOperationProperties {
  api: {
    displayName: string;
    iconUri: string;
    brandColor: string;
    description?: string;
  };
  summary: string;
  description?: string;
  visibility?: string;
  brandColor?: string;
  iconUri?: string;
  manifest?: ServiceProviderOperationManifest;
}

/**
 * Convert a Service Provider operation response to an OperationManifest.
 * This handles operations fetched from:
 * /runtime/webhooks/workflow/api/management/operationGroups/{group}/operations/{operationId}
 */
export function serviceProviderOperationToManifest(
  operationProperties: ServiceProviderOperationProperties,
  isTrigger: boolean = false
): OperationManifest {
  const { api, summary, description, manifest } = operationProperties;

  // Convert settings from the manifest format to our format
  const settings: OperationManifest['properties']['settings'] = {};

  if (manifest?.settings) {
    const s = manifest.settings;

    if (s.secureData) {
      settings.secureData = {};
    }
    if (s.trackedProperties?.scopes) {
      settings.trackedProperties = {
        scopes: s.trackedProperties.scopes.map(scope =>
          scope.toLowerCase() as 'action' | 'trigger'
        ),
      };
    }
    if (s.retryPolicy?.scopes) {
      settings.retryPolicy = {
        scopes: s.retryPolicy.scopes.map(scope =>
          scope.toLowerCase() as 'action' | 'trigger'
        ),
      };
    }
    if (s.timeout?.scopes) {
      settings.timeout = {
        scopes: s.timeout.scopes.map(scope =>
          scope.toLowerCase() as 'action' | 'trigger'
        ),
      };
    }
    if (s.concurrency?.scopes) {
      settings.concurrency = {
        scopes: s.concurrency.scopes.map(scope =>
          scope.toLowerCase() as 'action' | 'trigger'
        ),
      };
    }
  }

  // Following LogicAppsUX pattern: spread the entire manifest, then override specific properties
  // This ensures all manifest fields (inputs, outputs, inputsLocation, etc.) are included
  const manifestResult: OperationManifest = {
    properties: {
      // Spread all manifest properties first (inputs, outputs, inputsLocation, etc.)
      ...(manifest as Record<string, unknown>),
      // Then override with our normalized values
      iconUri: operationProperties.iconUri ?? api.iconUri ?? '',
      brandColor: operationProperties.brandColor ?? api.brandColor ?? '#0078D4',
      description: description ?? api.description ?? '',
      summary: summary ?? api.displayName ?? '',
      settings,
      // Service providers always require connections
      connection: {
        required: true,
      },
    },
  };

  // Add trigger property if this is a trigger operation
  if (isTrigger) {
    manifestResult.properties.trigger = 'single';
  }

  return manifestResult;
}

// =============================================================================
// SPLIT-ON SETTING VALUE
// =============================================================================

/**
 * Helper to recursively find all array properties in a schema.
 * Returns an array of paths to all array properties found.
 *
 * @param schema - The schema to search
 * @param currentPath - The path accumulated so far
 * @returns Array of paths to all array properties found
 */
function findAllArrayProperties(
  schema: SchemaObject | undefined,
  currentPath: string[] = []
): string[][] {
  if (!schema) return [];

  const results: string[][] = [];

  // If this schema is an array, add this path
  if (schema.type === 'array') {
    results.push(currentPath);
  }

  // Search through properties for more arrays
  if (schema.properties) {
    for (const [propName, propSchema] of Object.entries(schema.properties)) {
      const nestedResults = findAllArrayProperties(propSchema, [...currentPath, propName]);
      results.push(...nestedResults);
    }
  }

  return results;
}

/**
 * Get all possible splitOn expressions from an operation manifest.
 *
 * Analyzes the manifest's output schema to find all top-level array properties
 * and generates the appropriate expressions for use in a dropdown:
 * - If array is inside body property: `${triggerBody()?.['propertyName']}`
 * - If array is directly in outputs.properties: `${triggerOutputs()?.['propertyName']}`
 *
 * Expressions are emitted in Otto's JavaScript `${...}` syntax (not legacy `@{...}`).
 *
 * @param manifest - The operation manifest containing output schema
 * @returns Array of splitOn expressions, empty array if no array properties found
 *
 * @example
 * // For a manifest with outputs.properties.body.properties.value (array)
 * getAllSplitOns(manifest) // Returns ["${triggerBody()?.['value']}"]
 *
 * @example
 * // For a manifest with multiple arrays
 * getAllSplitOns(manifest) // Returns ["${triggerBody()?.['messages']}", "${triggerOutputs()?.['items']}"]
 */
export function getAllSplitOns(manifest: OperationManifest): string[] {
  const outputs = manifest.properties?.outputs;
  if (!outputs?.properties) {
    return [];
  }

  const splitOns: string[] = [];

  // Check if the array is inside the body property
  const bodySchema = outputs.properties.body;
  if (bodySchema) {
    // Check if body itself is an array
    if (bodySchema.type === 'array') {
      splitOns.push('${triggerBody()}');
    }

    // Check for arrays nested inside body.properties
    if (bodySchema.properties) {
      const pathsInBody = findAllArrayProperties(bodySchema, []);
      for (const path of pathsInBody) {
        if (path.length > 0) {
          const accessors = path.map(optionalBracketAccessor).join('');
          splitOns.push(`\${triggerBody()${accessors}}`);
        }
      }
    }
  }

  // Check for arrays directly in outputs.properties (not under body)
  for (const [propName, propSchema] of Object.entries(outputs.properties)) {
    if (propName === 'body') continue; // Already checked body above

    // Check if this property itself is an array
    if (propSchema?.type === 'array') {
      splitOns.push(`\${triggerOutputs()${optionalBracketAccessor(propName)}}`);
    }

    // Check for nested arrays within this property
    const pathsInProp = findAllArrayProperties(propSchema, []);
    for (const path of pathsInProp) {
      if (path.length > 0) {
        const accessors = [propName, ...path].map(optionalBracketAccessor).join('');
        splitOns.push(`\${triggerOutputs()${accessors}}`);
      }
    }
  }

  return splitOns;
}
